import { useState, useEffect, useCallback, useMemo } from 'react';
import Auth from '../../../middleware/auth';
import config from '../../../config';
import { saveAs } from 'file-saver';
import { toast } from '../../widgets/alerts/Toast';

const usePayrollData = () => {
  const currentCompany = sessionStorage.getItem('company');
  const currentUserType = sessionStorage.getItem('tipo');
  const state = Auth.isAuthenticated();

  const today = new Date();
  const dd = String(today.getDate()).padStart(2, '0');
  const mm = String(today.getMonth() + 1).padStart(2, '0');
  const yyyy = today.getFullYear();
  const todayFormat = yyyy + "-" + mm + "-" + dd;

  // Estados principales
  const [documentos, setDocumentos] = useState([]);
  const [selectedItems, setSelectedItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [approving, setApproving] = useState(false);
  const [periodosConNomina, setPeriodosConNomina] = useState([]);
  const [provider, setProvider] = useState("");
  const [visObsPDF, setVisObsPDF] = useState(false);
  const [showFilters, setShowFilters] = useState(true);

  // Estados de filtros
  const [filters, setFilters] = useState({
    periodo: "",
    numero_inicial: "",
    numero_final: "",
    fecha_inicial: todayFormat,
    fecha_final: todayFormat,
  });

  // Estados de búsqueda
  const [searchFilters, setSearchFilters] = useState({
    searchTipo: "",
    searchPeriodo: "",
    searchNumero: "",
    searchCliente: "",
    searchTransaccion: "",
    searchEstado: "",
  });

  // Estados de autenticación
  const [authData] = useState({
    empresa: currentCompany,
    tipo: currentUserType,
    isLogin: state,
  });

  // Cargar parámetros de empresa al montar el componente
  useEffect(() => {
    const fetchCompanyParameters = async () => {
      try {
        const response = await fetch(config.servBackEnd + '/companies/parameters/', {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'empresa': authData.empresa,
          }
        });

        const data = await response.json();
        if (data.ok) {
          const datos = data.data[0];
          setProvider(datos.PROVEEDOR_NOMINA);
        }
      } catch (error) {
        console.error(error);
      }
    };

    fetchCompanyParameters();
  }, [authData.empresa]);

  // Función para actualizar información
  // overrides: filtros que se aplican ya mismo (setFilters todavía no se ha reflejado en este render)
  const updateInfo = useCallback(async ({ target }, showspinner = true, clearSelection = true, overrides = {}) => {
    if (target) target.disabled = true;
    if (showspinner) setLoading(true);

    // Deseleccionar todos los elementos al buscar
    if (clearSelection) {
      setSelectedItems([]);
    }

    const f = { ...filters, ...overrides };
    const busqueda = {
      fecha_ini: f.fecha_inicial,
      fecha_fin: f.fecha_final,
      numero_inicial_nom: f.numero_inicial,
      numero_final_nom: f.numero_final,
      periodo: f.periodo,
    };

    try {
      const response = await fetch(config.servBackEnd + '/payroll/', {
        method: 'POST',
        body: JSON.stringify(busqueda),
        headers: {
          'Content-Type': 'application/json',
          'empresa': authData.empresa,
          'tipo': authData.tipo,
        }
      });

      const data = await response.json();
      if (data.ok) {
        setDocumentos(data.data);
      } else if (data.code === "0000") {
        setDocumentos([]);
      }
    } catch (error) {
      console.error(error);
    } finally {
      setLoading(false);
      if (target) target.disabled = false;
    }
  }, [filters, authData]);

  // Función para obtener PDF
  const getPDF = useCallback(async ({ target }, doc) => {
    target.disabled = true;
    const id = doc.ID_CABECERA_NOMINA;
    const tipo = doc.TIPO_DOCUMENTO_NOMINA;
    const numero = doc.NUMERO_DOCUMENTO_NOMINA;
    const nombreArchivo = tipo + '-' + numero + '.pdf';

    try {
      const response = await fetch(config.servBackEnd + '/files/getpdfnomina/' + id, {
        method: 'GET',
        headers: {
          'Content-Type': 'blob',
          'empresa': authData.empresa,
          'hist': '1'
        }
      });

      if (response.ok) {
        const blob = await response.blob();
        const file = new Blob([blob], { type: 'application/pdf' });
        saveAs(file, nombreArchivo);
      }
    } catch (error) {
      console.error(error);
    } finally {
      target.disabled = false;
    }
  }, [authData.empresa]);

  // Función para enviar email
  const mailerDoc = useCallback(async (e, doc) => {
    const { target } = e;
    target.disabled = true;

    if (doc.ID_CABECERA_NOMINA) {
      const docSend = [{ ID_FACTURA: doc.ID_CABECERA_NOMINA }];

      try {
        const response = await fetch(config.servBackEnd + '/files/createsendnomina/', {
          method: 'POST',
          body: JSON.stringify(docSend),
          headers: {
            'Content-Type': 'application/json',
            'empresa': authData.empresa
          }
        });

        const data = await response.json();
        setDocumentos(prevDocs =>
          prevDocs.map(elementDoc =>
            elementDoc.ID_CABECERA_NOMINA.toString() === data.ID_FACTURA.toString()
              ? { ...elementDoc, DOCUMENTO_ENVIADO: data.estado, DOCUMENTO_ENVIADO_OBSERVACION: data.mensaje }
              : elementDoc
          )
        );
      } catch (error) {
        console.error(error);
      }
    }
    target.disabled = false;
  }, [authData.empresa]);

  // Función para eliminar documento
  const deletedoc = useCallback(async (e, currentdata) => {
    try {
      const response = await fetch(
        config.servBackEnd + '/payroll/deletepayroll/' + currentdata.ID_CABECERA_NOMINA,
        {
          method: 'GET',
          headers: {
            'Content-Type': 'application/json',
            'empresa': authData.empresa,
          }
        }
      );

      const data = await response.json();
      if (data.ok) {
        alert("Tarea finalizada correctamente");
        updateInfo(e, false);
      } else {
        alert("No se pudo completar la actividad");
      }
    } catch (error) {
      console.error(error);
    }
  }, [authData.empresa, updateInfo]);

  // Función para reprocesar
  const limpiarNomina = useCallback(async (e, doc, targetState = "5") => {
    const { target } = e;
    if (target) target.disabled = true;
    const id = doc.ID_CABECERA_NOMINA;
    const datosUpdate = { newDocumentoEnviado: targetState, newObservacion: "" };

    try {
      const response = await fetch(config.servBackEnd + '/payroll/clear/' + id, {
        method: 'POST',
        body: JSON.stringify(datosUpdate),
        headers: {
          'Content-Type': 'application/json',
          'empresa': authData.empresa,
          'tipo': authData.tipo
        }
      });
      const data = await response.json();
      if (data.ok || data.estado || data.mensaje) {
        setDocumentos(prevDocs =>
          prevDocs.map(elementDoc =>
            elementDoc.ID_CABECERA_NOMINA.toString() === id.toString()
              ? { ...elementDoc, DOCUMENTO_ENVIADO: targetState, DOCUMENTO_ENVIADO_OBSERVACION: "" }
              : elementDoc
          )
        );
      }
    } catch (error) {
      console.error(error);
    } finally {
      if (target) target.disabled = false;
    }
  }, [authData.empresa, authData.tipo]);

  // Función para enviar API de documentos
  const sendDocumentApi = useCallback(async ({ url, method }) => {
    try {
      const response = await fetch(config.servBackEnd + url, {
        method,
        headers: {
          'Content-Type': 'application/json',
          'empresa': authData.empresa,
        }
      });

      const data = await response.json();
      setDocumentos(prevDocs =>
        prevDocs.map(docItem => {
          if (docItem.ID_CABECERA_NOMINA.toString() === data.idFactura.toString()) {
            let updatedDocItem = { ...docItem };
            if (data.hasOwnProperty('estado')) updatedDocItem.DOCUMENTO_ENVIADO = data.estado;
            if (data.hasOwnProperty('idTransaccion')) updatedDocItem.ID_TRANSACCION = data.idTransaccion || '0';
            if (data.hasOwnProperty('mensaje')) updatedDocItem.DOCUMENTO_ENVIADO_OBSERVACION = data.mensaje;
            return updatedDocItem;
          }
          return docItem;
        })
      );
    } catch (error) {
      console.log('sendDocumentApi', JSON.stringify(error));
    }
  }, [authData.empresa]);

  // Función para enviar payroll
  const enviarPayroll = useCallback(async ({ target }, currentData) => {
    target.disabled = true;
    let url = "";
    let method = 'POST';

    if (provider === 'comfiar') {
      url = '/payroll/sendDocuments/' + currentData.ID_CABECERA_NOMINA;
    } else if (provider === 'teso') {
      url = '/payroll/v2/sendDocuments/' + currentData.ID_CABECERA_NOMINA;
      method = 'GET';
    } else if (provider === 'radian') {
      url = '/payroll/v3/sendDocuments/' + currentData.ID_CABECERA_NOMINA;
      method = 'GET';
    }

    await sendDocumentApi({ url, method });
    target.disabled = false;
  }, [provider, sendDocumentApi]);

  // Función para confirmar payroll
  const confirmPayroll = useCallback(async ({ target }, currentData) => {
    target.disabled = true;
    let method = 'POST';
    let url = "";

    if (provider === 'comfiar') {
      url = '/payroll/salidaTran/' + currentData.ID_CABECERA_NOMINA;
    } else if (provider === 'teso') {
      url = '/payroll/v2/confirmDocuments/' + currentData.ID_CABECERA_NOMINA;
      method = 'GET';
    } else if (provider === 'radian') {
      url = '/payroll/v3/salidaTran/' + currentData.ID_CABECERA_NOMINA;
      method = 'GET';
    }

    await sendDocumentApi({ url, method });
    target.disabled = false;
  }, [provider, sendDocumentApi]);

  const selectedItemsSet = useMemo(() => {
    return new Set(selectedItems.map(item => item.ID_CABECERA_NOMINA));
  }, [selectedItems]);

  // Función para verificar si está seleccionado
  const isSelected = useCallback((id) => {
    return selectedItemsSet.has(id.toString());
  }, [selectedItemsSet]);

  // Función para filtrar documentos
  const filteredDocuments = useMemo(() => {
    return documentos.filter(currentData => {
      return (
        currentData.TIPO_DOCUMENTO_NOMINA.toLowerCase().includes(searchFilters.searchTipo.toLowerCase()) &&
        currentData.NUMERO_DOCUMENTO_NOMINA.toString().includes(searchFilters.searchNumero.toLowerCase()) &&
        currentData.NOMBRE_EMPLEADO.toLowerCase().includes(searchFilters.searchCliente.toLowerCase()) &&
        currentData.ID_TRANSACCION.toLowerCase().includes(searchFilters.searchTransaccion.toLowerCase()) &&
        currentData.TELEFONO_EMPRESA.toLowerCase().includes(searchFilters.searchPeriodo.toLowerCase()) &&
        (currentData.DOCUMENTO_ENVIADO.toLowerCase().includes(searchFilters.searchEstado.toLowerCase()) || searchFilters.searchEstado === "")
      );
    });
  }, [documentos, searchFilters]);

  // Función para seleccionar todos
  const handleSelectAll = useCallback((e, visibleRows = []) => {
    const { checked } = e.target;

    if (checked) {
      const allItems = visibleRows.map(item => ({
        ID_CABECERA_NOMINA: item.ID_CABECERA_NOMINA.toString(),
        STATE: item.DOCUMENTO_ENVIADO
      }));
      setSelectedItems(allItems);
    } else {
      setSelectedItems([]);
    }
  }, []);

  // Función para cambio de checkbox individual
  const handleCheckboxChange = useCallback((e, item) => {
    const { checked, id } = e.target;
    setSelectedItems(prevState => {
      if (checked) {
        return [...prevState, { ID_CABECERA_NOMINA: id, STATE: item.DOCUMENTO_ENVIADO }];
      } else {
        return prevState.filter(selectedItem => selectedItem.ID_CABECERA_NOMINA !== id.toString());
      }
    });
  }, []);

  // Consulta EMPRESAS.AUTO_SEND_NOMINA; si no se puede consultar se asume desactivado
  const isAutoSendEnabled = useCallback(async () => {
    try {
      const response = await fetch(config.servBackEnd + '/payroll/autosendstatus/' + currentCompany, {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', 'empresa': currentCompany }
      });
      const params = await response.json();
      const p = params && params.ok && params.data && params.data[0];
      return !!p && (p.AUTO_SEND_NOMINA === 1 || p.AUTO_SEND_NOMINA === "1" || p.AUTO_SEND_NOMINA === true);
    } catch (err) {
      console.error("Error validando AUTO_SEND_NOMINA", err);
      return false;
    }
  }, [currentCompany]);

  // Función para aprobar masivamente pasándolos a estado 0
  const massiveApprove = useCallback(async () => {
    setApproving(true);
    try {
      // Sin envío automático el documento quedaría en 0 sin que nadie lo envíe: no se aprueba
      if (!(await isAutoSendEnabled())) {
        toast({
          title: 'Envío automático desactivado',
          description: 'La empresa no tiene activo el envío automático de nómina, por lo que los documentos no fueron aprobados. Comuníquese con soporte para su activación.',
          type: 'warning',
          position: 'bottom-center',
          duration: 0
        });
        return;
      }

      const itemsToApprove = selectedItems.filter(element => {
        const currentDoc = documentos.find(doc => String(doc.ID_CABECERA_NOMINA) === String(element.ID_CABECERA_NOMINA));
        return currentDoc && String(currentDoc.DOCUMENTO_ENVIADO) === "5" && Number(currentDoc.VALID) === 0;
      });

      if (itemsToApprove.length > 0) {
        await Promise.all(
          itemsToApprove.map(element => limpiarNomina({ target: {} }, { ID_CABECERA_NOMINA: element.ID_CABECERA_NOMINA }, "0"))
        );

        setSelectedItems([]);
        toast({
          title: '¡Aprobación Exitosa!',
          description: `${itemsToApprove.length} documentos han sido aprobados para envío en segundo plano.`,
          type: 'successful',
          position: 'bottom-center',
          duration: 20
        });
      }
    } finally {
      setApproving(false);
    }
  }, [selectedItems, limpiarNomina, documentos, isAutoSendEnabled]);

  // Últimos 4 periodos (lapso YYYYMM) hasta el mes actual, para los filtros rápidos
  const ultimosPeriodos = [3, 2, 1, 0].map((i) => {
    const d = new Date(today.getFullYear(), today.getMonth() - i, 1);
    return `${d.getFullYear()}${String(d.getMonth() + 1).padStart(2, '0')}`;
  });

  // Filtro rápido: consulta el periodo completo (sin fechas); al quitarlo vuelve a la fecha de hoy
  const selectPeriodo = useCallback((periodo) => {
    const nuevo = filters.periodo === periodo
      ? { periodo: '', fecha_inicial: todayFormat, fecha_final: todayFormat }
      : { periodo, fecha_inicial: '', fecha_final: '' };
    setFilters(prev => ({ ...prev, ...nuevo }));
    updateInfo({ target: null }, true, true, nuevo);
  }, [filters.periodo, todayFormat, updateInfo]);

  // Periodos con nóminas de la empresa (más reciente primero); al entrar se abre el más reciente
  useEffect(() => {
    if (!authData.isLogin || !authData.empresa) return;
    fetch(config.servBackEnd + '/payroll/periodos', {
      method: 'GET',
      headers: { 'Content-Type': 'application/json', 'empresa': authData.empresa }
    })
      .then(r => r.json())
      .then(data => {
        const lista = data.ok ? data.data.map(x => x.PERIODO) : [];
        setPeriodosConNomina(lista);
        if (lista.length) selectPeriodo(lista[0]);
      })
      .catch(err => console.error('Error consultando periodos de nómina', err));
    // Solo al entrar o cambiar de empresa
  }, [authData.empresa]);

  // Función para actualizar filtros
  const updateFilter = useCallback((name, value) => {
    setFilters(prev => ({ ...prev, [name]: value }));
  }, []);

  // Función para actualizar filtros de búsqueda
  const updateSearchFilter = useCallback(({ target }) => {
    const { name, value } = target;
    setSearchFilters(prev => ({ ...prev, [name]: value }));
  }, []);

  // Polling dinámico: 7 segundos si hay documentos sin procesar (0) o pendientes (1), 30 segundos por defecto
  useEffect(() => {
    if (!authData.isLogin || !authData.empresa) return;

    const needsFastPolling = documentos.some(doc => String(doc.DOCUMENTO_ENVIADO) === "0" || String(doc.DOCUMENTO_ENVIADO) === "1");
    const intervalTime = needsFastPolling ? 7000 : 30000;

    const intervalId = setInterval(() => {
      updateInfo({ target: null }, false, false);
    }, intervalTime);

    return () => clearInterval(intervalId);
  }, [documentos, updateInfo, authData.isLogin, authData.empresa]);

  return {
    // Estados
    documentos,
    selectedItems,
    loading,
    approving,
    provider,
    visObsPDF,
    showFilters,
    filters,
    searchFilters,
    authData,

    // Funciones de datos
    filteredDocuments: filteredDocuments,

    // Funciones de acciones
    updateInfo,
    getPDF,
    mailerDoc,
    deletedoc,
    enviarPayroll,
    confirmPayroll,
    limpiarNomina,

    // Funciones de selección
    isSelected,
    handleSelectAll,
    handleCheckboxChange,
    massiveApprove,

    // Funciones de filtros
    ultimosPeriodos,
    periodosConNomina,
    selectPeriodo,
    updateFilter,
    updateSearchFilter,

    // Funciones de UI
    setVisObsPDF,
    setShowFilters,
  };
};

export default usePayrollData;


// WEBPACK FOOTER //
// ./src/components/nomina/hooks/usePayrollData.js